import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { ConfigProvider, Layout, Spin } from '@didi-pebble/pebble-design-react';
import { useRoutes, useNavigate, useLocation } from 'react-router-dom';
import { useSetAtom, useAtomValue } from 'jotai';
import { i18nStart, i18nSetLang, i18nGet } from '@didi/i18n-plugin';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { init } from '@didi/elvish-lib-javascript';
import zhCN from 'antd/es/locale/zh_CN';
import enUS from 'antd/es/locale/en_US';
import ptBR from 'antd/es/locale/pt_BR';

import localesIndex from '@/locales/i18n-index';
import localesInfo from '@/locales/i18n-info';
import { getMenuMap } from '@/config';
import { HeaderComp, SiderComp, HeaderBreadcrumb } from '@/components';
import { loginInfoAtom, setUserAuth, errnoAtom, menuAtom, setFilterList } from '@/store';
import { Omega } from '@/lib';
import { useIsMobile } from '@/utils';

import { ROUTER_CONFIG } from './router';

const { Content } = Layout;

const providerLocale: any = {
  'en-US': enUS,
  'zh-CN': zhCN,
  'pt-BR': ptBR
};

// dayjs多语言
dayjs.locale('zh-cn');
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);

const App: React.FC = () => {
  const navigate = useNavigate();
  const appRoutesElement = useRoutes(ROUTER_CONFIG);
  const loginInfo: any = useAtomValue(loginInfoAtom);
  const errno: any = useAtomValue(errnoAtom);
  const menuList: any = useAtomValue(menuAtom);
  const queryUserAuth = useSetAtom(setUserAuth);
  const queryFilterList = useSetAtom(setFilterList);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    (async () => {
      await queryUserAuth();
      queryFilterList();
    })();

    /**
     * @description: 解决在移动端浏览器中，H5页面的100vh不为真正视口高度问题
     * 100vh会包含浏览器的地址栏、搜索栏
     * window.innerHeight才能拿到真正的视口高度，用它来计算得到真正的100vh
     * 在tailwind.config.js中设置计算公式得到100vh，以供各个页面使用
     */
    const setVhVariable = () => {
      const vh = window.innerHeight * 0.01; // 1vh = {xx}px

      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // 在组件第一次挂载时设置CSS变量
    setVhVariable();

    // 在窗口大小变化时更新CSS变量
    window.addEventListener('resize', setVhVariable);

    // 在组件卸载时移除事件监听器
    return () => {
      window.removeEventListener('resize', setVhVariable);
    };
  }, []);

  useEffect(() => {
    if (location?.pathname && loginInfo?.uid) {
      Omega.sendPageView();
    }
  }, [location]);

  const checkLang = () => {
    const devTool: any = localStorage.getItem('devTool'); // 测试时可转语言
    const langList = Object.keys(providerLocale);

    if (langList.some((item: any) => item.toLowerCase() === devTool?.toLowerCase())) {
      return langList.find((item: any) => item.toLowerCase() === devTool?.toLowerCase());
    }
    return loginInfo?.lang || 'pt-BR';
  };

  const setUserConfig = async () => {
    const nowLang: string = checkLang();

    i18nSetLang(nowLang); // 'pt-BR', 'en-US', 'zh-CN'
    i18nStart(localesIndex, localesInfo);
    try {
      await init(loginInfo?.lang || 'pt-BR', loginInfo?.country_code || 'BR'); // elvish init 必须await
    } catch (error) {
      console.log(error);
    }
    i18nGet((localesIndex as any)?.[nowLang?.toLowerCase()], nowLang);
    // 注入omega参数
    Omega.setConfig({
      userId: loginInfo?.uid,
      countryCode: loginInfo?.country_code
    });

    const menuMap = getMenuMap();
    // 所有路由（二级栏+三级栏）集合
    const allSubMenuList: any = Object.keys(menuMap)?.reduce((accum: any, item: any) => {
      return [...accum, ...menuMap[item].allRoute];
    }, []);

    const inAllHash = allSubMenuList?.some((item: any) => item === location.pathname);

    if (inAllHash) {
      Omega.sendPageView();
    }

    // 获取当前hash对应的key
    const nowHashKey = Object.keys(menuMap)?.find(key => {
      return menuMap[key]?.allRoute.includes(location.pathname);
    });

    // 判断当前hash是否存在于后端下发的侧边栏权限中
    const hashInMenu = menuList.some((i: any) =>
      i?.menu_auth?.some((j: any) => j?.submenu_name === nowHashKey)
    );

    // 当hash为''、'/'、'/noPermission' 或者 当前hash不存在于侧边栏中 并且 errno===0 的时候，自动跳转到第一个有权限的二级页面
    if (!hashInMenu && errno === 0) {
      const toPage = menuList.find((i: any) => i?.menu_auth?.length > 0)?.menu_auth?.[0]
        ?.submenu_name;

      navigate(menuMap?.[toPage]?.subRoute, { replace: true });
    }

    setLoading(false);
  };

  useEffect(() => {
    if (loginInfo?.lang) {
      setUserConfig();
    }
  }, [loginInfo]);

  const [contentKey, setContentKey] = useState<string>('');

  useEffect(() => {
    setContentKey(`${location?.pathname}-${location?.search}`);
  }, [location]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorBgLayout: '#fff'
        },
        components: {
          Layout: {
            colorBgHeader: '#fff'
          },
          Collapse: {
            colorBgContainer: '#F8F8FA',
            colorBorder: '#E9EAEE'
          },
          Menu: {
            colorItemBg: '#E9EAEE',
            colorSubItemBg: '#E9EAEE'
          }
        }
      }}
      locale={providerLocale[loginInfo?.lang] || ptBR}
    >
      <Layout className='h-100vh flex flex-col'>
        {loading ? (
          <Spin className='h-100vh flex justify-center items-center' />
        ) : (
          <>
            <HeaderComp />
            <Layout className='bg-[#E9EAEE] flex-1 overflow-hidden'>
              <SiderComp />
              <Content
                key={contentKey}
                className={classnames('flex flex-col bg-[#fff] overflow-hidden', {
                  'rounded-[8px] mb-[32px] mr-[24px]': !isMobile
                })}
              >
                {!isMobile && <HeaderBreadcrumb />}
                <div
                  className={classnames('flex-1 overflow-scroll no-scrollbar pb-[20px]', {
                    'px-[28px]': !isMobile,
                    'px-[8px]': isMobile
                  })}
                >
                  {appRoutesElement}
                </div>
              </Content>
            </Layout>
          </>
        )}
      </Layout>
    </ConfigProvider>
  );
};

export default App;
